import { useSuspenseQuery } from '@tanstack/react-query';
import { ISbStoryData } from 'storyblok-js-client';

import { getSlugWithDash } from '@natu/utils/getSlugWithDash';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import { getStoryblokSdk } from '../../../../../api/api';
import { useDraftModeContext } from '../../../../../storyblok-preview';

type GetCategoryBarItemsInput = {
  categoryItems: string[];
  draftMode: boolean;
};

type CategoryItem = {
  uuid: string;
  name: string;
  fullSlug: string;
};

export const getCategoryBarQueryKey = ({ categoryItems, draftMode }: GetCategoryBarItemsInput) => [
  'categoryBar',
  categoryItems,
  { draftMode },
];

export const getCategoryBarItems = async ({
  categoryItems,
  draftMode,
}: GetCategoryBarItemsInput): Promise<CategoryItem[]> => {
  const { getArticleCategoryNodes } = getStoryblokSdk({ draftMode });

  const { data } = await getArticleCategoryNodes({
    startsWith: 'blog',
    filterQuery: {
      component: {
        in: 'articleCategory',
      },
    },
    byUuidsOrdered: categoryItems.join(','),
    excludingFields: 'body,customPreHeder,customHeader,seo,disablePreHeader',
  });

  return data?.stories.map((story: ISbStoryData) => ({
    uuid: story?.uuid,
    name: story?.name,
    fullSlug: getSlugWithDash(story?.full_slug),
  }));
};

type UseCategoryBarInput = {
  categoryItems: GetCategoryBarItemsInput['categoryItems'];
};

export const useCategoryBar = ({ categoryItems }: UseCategoryBarInput) => {
  const { draftMode } = useDraftModeContext();

  const { data } = useSuspenseQuery({
    queryKey: getCategoryBarQueryKey({ categoryItems, draftMode }),
    queryFn: async () => getCategoryBarItems({ categoryItems, draftMode }),
  });

  return {
    data,
    isEmpty: !isArrayWithLength(data),
  };
};
